import { z } from 'zod';
import { investigationCompletionStatesSchema } from './saved-investigation-state';
import type { AskHelixEvidenceRefDto, AskHelixNumericBindingDto } from '../../ask-helix';
import type { EvidenceIdentity } from '../../evidence/evidence-tool.contract';
import { RUNTIME_V3_DEFINITION_KINDS, RUNTIME_V3_DEFINITION_SCOPE_KINDS,
  type RuntimeV3DefinitionVersionRef } from '../../runtime-v3/definitions';
import { ASK_VIEW_IDS } from '../views/view-payload';
import { savedPublicBindingSchema, savedPublicEvidenceSchema } from './saved-investigation-public-fields';
import { revenueAddressSchema, revenueSubjectSchema, revenueSubjectMatchesAddress,
  revenueCutDescriptorSchema, revenueSavedReadAddressSchema }
  from '../../runtime-v3/revenue-figure-cut';
import { objectiveInvestigationReadRequestSchema, objectiveInvestigationReadRequestShape }
  from '../../runtime-v3/objective-contract.schema';
import { objectiveCutDescriptorSchema, objectiveInvestigationSubjectSchema,
  objectiveInvestigationTargetSchema, objectiveViewAddressSchema } from '../../runtime-v3/objective-contract-schema';
import type { ObjectiveInvestigationSubject, ObjectiveViewAddress,
  ObjectiveSavedReadAddress, ObjectiveCutDescriptor } from '../../runtime-v3/objective-contract';

export const INVESTIGATION_CONTEXT_KIND = 'analysis_investigation';
export const SAVED_INVESTIGATION_SCHEMA_VERSION = 'analysis-investigation.v1';
const text = z.string().min(1);
const unknownMetadata = z.object({ kind: z.literal('unknown') }).strict();
const knownString = z.object({ kind: z.literal('known'), value: text }).strict();
const stringMetadata = z.union([knownString, unknownMetadata]);
const versionMetadata = z.union([
  z.object({ kind: z.literal('known'), value: z.number().int().positive() }).strict(), unknownMetadata,
]);

const legacyAddressSchema = z.object({
  dealId: text, asOf: z.string().date(), viewId: z.enum(ASK_VIEW_IDS), params: z.record(z.string()),
}).strict();
const legacySubjectSchema = z.object({
  kind: z.literal('commitment'), ref: text, dealId: text, scopeRef: text.nullable(),
}).strict();
export const savedInvestigationAddressSchema = z.union([legacyAddressSchema, revenueAddressSchema, objectiveViewAddressSchema]);
const existingSubjectSchema = z.discriminatedUnion('kind', [legacySubjectSchema, revenueSubjectSchema]);
export const savedInvestigationSubjectSchema = z.discriminatedUnion('kind', [legacySubjectSchema,
  revenueSubjectSchema, objectiveInvestigationSubjectSchema]);
export type SavedInvestigationSubject = z.infer<typeof savedInvestigationSubjectSchema>;
const legacyReadRequestSchema = z.object({
  address: legacyAddressSchema,
  subject: legacySubjectSchema.pick({ kind: true, ref: true }),
}).strict();
const revenueReadAddressSchema = z.union([revenueAddressSchema, revenueSavedReadAddressSchema]);
const revenueReadRequestShape = z.object({ address: revenueReadAddressSchema,
  subject: revenueSubjectSchema.pick({ kind: true, ref: true }) }).strict();
const revenueReadRequestSchema = revenueReadRequestShape.refine(value =>
  value.subject.ref === value.address.goalVersionRef);
export const objectiveSavedInvestigationReadRequestSchema = objectiveInvestigationTargetSchema;
export type ObjectiveSavedInvestigationReadRequest = Readonly<{
  subject: ObjectiveInvestigationSubject; address: ObjectiveViewAddress | ObjectiveSavedReadAddress;
}>;
export const savedInvestigationReadRequestSchema = z.union([legacyReadRequestSchema, revenueReadRequestSchema,
  objectiveSavedInvestigationReadRequestSchema, objectiveInvestigationReadRequestSchema]);
export type SavedInvestigationReadRequest = z.infer<typeof savedInvestigationReadRequestSchema>;
export const objectiveSavedInvestigationIdRequestSchema = objectiveInvestigationTargetSchema.innerType()
  .extend({ artifactId: text }).refine(value => objectiveInvestigationTargetSchema.safeParse({
    subject: value.subject, address: value.address }).success);
export type ObjectiveSavedInvestigationIdRequest = ObjectiveSavedInvestigationReadRequest
  & Readonly<{ artifactId: string }>;
export const savedInvestigationIdRequestSchema = z.union([
  legacyReadRequestSchema.extend({ artifactId: text }),
  revenueReadRequestShape.extend({ artifactId: text }).refine(value => value.subject.ref === value.address.goalVersionRef),
  objectiveInvestigationReadRequestShape.extend({ artifactId: text }).refine(value => value.subject.ref === value.address.goalVersionRef),
  objectiveSavedInvestigationIdRequestSchema,
]);
export type SavedInvestigationIdRequest = z.infer<typeof savedInvestigationIdRequestSchema>;
const legacyRequestSchema = z.object({ kind: z.literal('five_why'), subject: legacySubjectSchema,
  address: legacyAddressSchema, expectedParentArtifactId: text.nullable() }).strict();
const revenueRequestSchema = z.object({ kind: z.literal('five_why'), subject: revenueSubjectSchema,
  address: revenueAddressSchema, expectedParentArtifactId: text.nullable() }).strict()
  .refine(value => revenueSubjectMatchesAddress(value.subject, value.address));
export const objectiveSavedInvestigationRequestSchema = z.object({ kind: z.literal('five_why'),
  subject: objectiveInvestigationSubjectSchema, address: objectiveViewAddressSchema,
  expectedParentArtifactId: text.nullable() }).strict().refine(value =>
    objectiveInvestigationTargetSchema.safeParse({ subject: value.subject, address: value.address }).success);
export type ObjectiveSavedInvestigationRequest = Readonly<{
  kind: 'five_why'; subject: ObjectiveInvestigationSubject; address: ObjectiveViewAddress;
  expectedParentArtifactId: string | null;
}>;
export const savedInvestigationRequestSchema = z.union([legacyRequestSchema, revenueRequestSchema,
  objectiveSavedInvestigationRequestSchema]);
export type SavedInvestigationRequest = z.infer<typeof savedInvestigationRequestSchema>;
const existingReferenceSchema = z.object({
  artifactId: text, investigationId: text, revision: z.number().int().positive(),
  address: z.union([legacyAddressSchema, revenueAddressSchema]), subject: existingSubjectSchema,
}).strict();
export const objectiveSavedInvestigationReferenceSchema = z.object({
  artifactId: text, investigationId: text, revision: z.number().int().positive(),
  address: objectiveViewAddressSchema, subject: objectiveInvestigationSubjectSchema,
}).strict().refine(value => objectiveInvestigationTargetSchema.safeParse({
  subject: value.subject, address: value.address }).success);
export type ObjectiveSavedInvestigationReference = Readonly<{
  artifactId: string; investigationId: string; revision: number;
  address: ObjectiveViewAddress; subject: ObjectiveInvestigationSubject;
}>;
export const savedInvestigationReferenceSchema = z.union([existingReferenceSchema,
  objectiveSavedInvestigationReferenceSchema]);
export type SavedInvestigationReference = z.infer<typeof savedInvestigationReferenceSchema>;
/** PR3 quotes the saved revision and its objective/cut rather than inventing another identity. */
export type ObjectiveInvestigationHandoff = ObjectiveSavedInvestigationReference;
export type ObjectiveActionHandoff = Readonly<{
  investigation: ObjectiveInvestigationHandoff; actionId: string;
}>;
export const objectiveInvestigationHandoffSchema = objectiveSavedInvestigationReferenceSchema;
export const objectiveActionHandoffSchema = z.object({
  investigation: objectiveInvestigationHandoffSchema, actionId: text,
}).strict() satisfies z.ZodType<ObjectiveActionHandoff>;

const factInput = z.union([
  z.object({ kind: z.literal('fact'), token: text }).strict(), unknownMetadata,
]);
const sidecarEntry = z.object({
  ordinal: z.number().int().nonnegative(), id: text, text,
  subjectRef: text, assertionRef: text,
  supportingFactTokens: z.array(text), contradictingFactTokens: z.array(text),
}).strict();
/** Offered metadata only. The producer compiles every text under the current FactAuthority. */
export const savedInvestigationSidecarSchema = z.object({
  findings: z.array(sidecarEntry), hypotheses: z.array(sidecarEntry),
  nextTest: z.object({ text, owner: factInput, timing: factInput,
    supportingFactTokens: z.array(text), contradictingFactTokens: z.array(text) }).strict(),
}).strict();
export type SavedInvestigationSidecar = z.infer<typeof savedInvestigationSidecarSchema>;

const evidenceIdentitySchema = z.object({
  dataset: text, pins: z.object({ revisionId: text, definitionId: text,
    definitionDigest: text, snapshot: text }).strict(),
  recordKey: z.record(text).optional(), field: text.optional(),
  relationshipType: text.optional(), edgeId: text.optional(),
}).strict() satisfies z.ZodType<EvidenceIdentity>;
const definitionRefShape = z.object({
  definitionRef: text,
  scopeKind: z.enum(RUNTIME_V3_DEFINITION_SCOPE_KINDS), tenantId: text.nullable(),
  digest: text.optional(), subset: z.object({ collection: text,
    keys: z.array(text).min(1), digest: text }).strict().optional(),
}).strict();
function validateDefinitionRef(pin: RuntimeV3DefinitionVersionRef, context: z.RefinementCtx): void {
  if ((pin.digest === undefined) === (pin.subset === undefined))
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Exactly one definition pin is required' });
  if ((pin.scopeKind === 'shared') !== (pin.tenantId === null))
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Definition scope and tenant disagree' });
}
export const savedInvestigationDefinitionRefSchema = definitionRefShape.superRefine(validateDefinitionRef);
const definitionPinSchema = definitionRefShape.extend({
  kind: z.enum(RUNTIME_V3_DEFINITION_KINDS), version: versionMetadata,
}).strict().superRefine(validateDefinitionRef);
const componentPinSchema = z.object({ id: text, version: stringMetadata, digest: stringMetadata }).strict();
const compiledEntry = z.object({
  ordinal: z.number().int().nonnegative(), id: text, text, subjectRef: text, assertionRef: text,
  supportingReceiptRefs: z.array(text), contradictingReceiptRefs: z.array(text),
}).strict();
const resolvedFact = z.union([
  z.object({ kind: z.literal('known'), value: text, evidenceRefs: z.array(text).min(1) }).strict(),
  unknownMetadata,
]);

/** Existing published DTOs are opaque here: this adapter is not a second numeric admission gate. */
function publishedObject<T>(): z.ZodType<T> {
  return z.custom<T>(value => value !== null && typeof value === 'object' && !Array.isArray(value));
}
const completionShape = z.object({
  subject: savedInvestigationSubjectSchema,
  figureCut: z.union([revenueCutDescriptorSchema, objectiveCutDescriptorSchema]).optional(),
  executionRunId: text, producedAt: z.string().datetime({ offset: true }), asOf: stringMetadata,
  dataPins: z.array(z.object({ datasetRef: text, revisionId: stringMetadata, digest: stringMetadata }).strict()),
  definitionPins: z.array(definitionPinSchema), skillPin: componentPinSchema,
  modelPin: z.union([z.object({ provider: text, id: text, version: stringMetadata }).strict(), unknownMetadata]),
  enginePins: z.array(componentPinSchema),
  receipts: z.array(z.object({ runId: text, resultDigest: text, evidenceRefs: z.array(text),
    executionTool: z.enum(['query', 'derive', 'read_records', 'read_related', 'cause_share']),
    executionReceipt: z.record(z.unknown()),
  }).strict()).min(1),
  sourceDependencies: z.array(evidenceIdentitySchema).min(1),
  answerMarkdown: text,
  numericBindings: z.array(publishedObject<AskHelixNumericBindingDto>()),
  evidence: z.array(publishedObject<AskHelixEvidenceRefDto>()),
  diagnostics: z.array(z.object({ index: z.number().int(), code: text, alignment: z.literal('failed').optional(),
    sourceRange: z.object({ start: z.number().int(), end: z.number().int() }).strict().optional() }).strict()),
  turnOutcome: z.literal('answered'), truncated: z.boolean(), interrupted: z.boolean(),
  refusal: z.object({ code: text, message: text }).passthrough().nullish(),
  supportedFindings: z.array(compiledEntry.extend({ basis: z.enum(['observed', 'supported']) })),
  competingHypotheses: z.array(compiledEntry.extend({ reasonCode: z.literal('EDGE_NOT_CAUSE').optional() })),
  nextTest: z.object({ text, owner: resolvedFact, timing: resolvedFact,
    supportingReceiptRefs: z.array(text), contradictingReceiptRefs: z.array(text) }).strict(),
}).strict();
function cutMatchesCompletion(value: z.infer<typeof completionShape>): boolean {
  if (value.subject.kind === 'commitment') return value.figureCut === undefined;
  const cut = value.figureCut;
  if (value.subject.kind === 'objective') return cut?.kind === 'objective'
    && cut.identity.goalVersionRef === value.subject.ref && cut.identity.tenantId === value.subject.tenantId
    && cut.identity.strategyId === value.subject.strategyId && cut.identity.scopeRef === value.subject.scopeRef
    && value.asOf.kind === 'known' && value.asOf.value === cut.asOf;
  return cut?.kind === 'revenue_goal' && cut.scope.goalVersionRef === value.subject.ref
    && cut.scope.companyRef === value.subject.companyRef && cut.scope.strategyId === value.subject.strategyId
    && cut.scope.goalGraphNodeRef === value.subject.scopeRef
    && value.asOf.kind === 'known' && value.asOf.value === cut.dates.asOf;
}
const existingCompletionSchema = completionShape.refine(
  value => !value.refusal && !value.truncated && !value.interrupted && cutMatchesCompletion(value), {
  message: 'Only a complete, non-refused publication can be saved',
});
const objectiveCompletionShape = completionShape.extend({ subject: objectiveInvestigationSubjectSchema,
  figureCut: objectiveCutDescriptorSchema });
const objectiveCompletionCutSchema = objectiveCompletionShape.pick({ subject: true, figureCut: true, asOf: true })
  .refine(value => value.subject.ref === value.figureCut.identity.goalVersionRef
    && value.subject.tenantId === value.figureCut.identity.tenantId
    && value.subject.strategyId === value.figureCut.identity.strategyId
    && value.subject.scopeRef === value.figureCut.identity.scopeRef
    && value.asOf.kind === 'known' && value.asOf.value === value.figureCut.asOf);
const objectiveCompletionSchema = objectiveCompletionShape.refine(value =>
  !value.refusal && !value.truncated && !value.interrupted && objectiveCompletionCutSchema.safeParse({
    subject: value.subject, figureCut: value.figureCut, asOf: value.asOf }).success);
export const savedInvestigationCompletionSchema = z.union([existingCompletionSchema, objectiveCompletionSchema]);
/** Only the trusted completed publisher supplies this, never an HTTP request body. */
export type SavedInvestigationCompletion = z.infer<typeof savedInvestigationCompletionSchema>;
export const savedInvestigationSaveInputSchema = z.object({
  tenantId: text, actorUserId: text, scopeRootNodeId: text.nullable(),
  expectedParentArtifactId: text.nullable(), completion: savedInvestigationCompletionSchema,
}).strict().refine(value => value.completion.subject.kind !== 'objective'
  || value.tenantId === value.completion.subject.tenantId
    && value.scopeRootNodeId === value.completion.subject.scopeRef);
export type SavedInvestigationSaveInput = z.infer<typeof savedInvestigationSaveInputSchema>;
const contentIdentityShape = {
  investigationId: text, revision: z.number().int().positive(), supersedesArtifactId: text.nullable(),
  dependencyDigest: text, contentDigest: text,
};
const existingContentSchema = completionShape.omit({ refusal: true }).extend(contentIdentityShape)
  .strict().refine(cutMatchesCompletion);
export const objectiveSavedInvestigationContentSchema = objectiveCompletionShape.omit({ refusal: true })
  .extend(contentIdentityShape).strict().refine(value => objectiveCompletionCutSchema.safeParse({
    subject: value.subject, figureCut: value.figureCut, asOf: value.asOf }).success);
export type ObjectiveSavedInvestigationContent = Omit<z.infer<typeof existingContentSchema>, 'subject' | 'figureCut'>
  & { subject: ObjectiveInvestigationSubject; figureCut: ObjectiveCutDescriptor };
export const savedInvestigationContentSchema = z.union([existingContentSchema, objectiveSavedInvestigationContentSchema]);
export type SavedInvestigationContent = z.infer<typeof savedInvestigationContentSchema>;
export const savedInvestigationRecordSchema = z.object({
  artifactId: text, tenantId: text, scopeRootNodeId: text.nullable(), createdByUserId: text,
  reviewStatus: z.literal('DRAFT'), content: savedInvestigationContentSchema,
}).strict().refine(value => value.content.subject.kind !== 'objective'
  || value.tenantId === value.content.subject.tenantId
    && value.scopeRootNodeId === value.content.subject.scopeRef);
export type SavedInvestigationRecord = z.infer<typeof savedInvestigationRecordSchema>;
export const savedInvestigationFreshnessSchema = z.object({
  state: z.enum(['FRESH', 'STALE', 'UNKNOWN']), checkedAt: z.string().datetime({ offset: true }),
  changedRefs: z.array(text),
}).strict();
export type SavedInvestigationFreshness = z.infer<typeof savedInvestigationFreshnessSchema>;
const publicEvidenceLinks = z.object({
  supportingEvidenceIds: z.array(text), contradictingEvidenceIds: z.array(text),
}).strict();
const publicResolvedFact = z.union([
  z.object({ kind: z.literal('known'), value: text, evidenceIds: z.array(text) }).strict(),
  unknownMetadata,
]);
export const savedInvestigationPublicArtifactSchema = z.object({
  figureCut: z.union([revenueCutDescriptorSchema, objectiveCutDescriptorSchema]).optional(),
  artifactId: text, revision: z.number().int().positive(), producedAt: z.string().datetime({ offset: true }),
  asOf: stringMetadata, answerMarkdown: text,
  numericBindings: z.array(savedPublicBindingSchema), evidence: z.array(savedPublicEvidenceSchema),
  supportedFindings: z.array(publicEvidenceLinks.extend({ text, basis: z.enum(['observed', 'supported']) }).strict()),
  competingHypotheses: z.array(publicEvidenceLinks.extend({ text,
    reasonCode: z.literal('EDGE_NOT_CAUSE').optional() }).strict()),
  nextTest: publicEvidenceLinks.extend({ text, owner: publicResolvedFact, timing: publicResolvedFact }).strict(),
}).strict();
export type SavedInvestigationPublicArtifact = z.infer<typeof savedInvestigationPublicArtifactSchema>;
export const savedInvestigationV2PublicArtifactSchema = savedInvestigationPublicArtifactSchema.extend({
  answerMarkdown: z.string(), completion: investigationCompletionStatesSchema,
  investigationState: z.enum(['COMPLETE', 'INCOMPLETE']),
}).superRefine((value, context) => {
  const complete = value.completion.evidence === 'complete' && value.completion.reasoning === 'complete'
    && value.completion.narration === 'complete' && value.answerMarkdown.length > 0;
  if ((value.investigationState === 'COMPLETE') !== complete)
    context.addIssue({ code: 'custom', path: ['investigationState'], message: 'COMPLETION_STATE_MISMATCH' });
});
export const savedInvestigationResponseSchema = z.object({
  artifact: z.union([savedInvestigationPublicArtifactSchema, savedInvestigationV2PublicArtifactSchema]),
  freshness: savedInvestigationFreshnessSchema.pick({ state: true, checkedAt: true }).strict(),
}).strict();
export type SavedInvestigationResponse = z.infer<typeof savedInvestigationResponseSchema>;

type PublicProjectors = Readonly<{
  binding: (value: AskHelixNumericBindingDto) => unknown;
  evidence: (value: AskHelixEvidenceRefDto) => AskHelixEvidenceRefDto;
  schema?: z.ZodType<SavedInvestigationResponse>;
  metadata?: Readonly<Record<string, unknown>>;
}>;
/** Publication fields only: projection does not decide investigation completion. */
type PublicationRecord = Omit<SavedInvestigationRecord, 'content'> & { content:
  Pick<SavedInvestigationContent, 'revision' | 'producedAt' | 'asOf' | 'answerMarkdown'
    | 'figureCut' | 'numericBindings' | 'evidence' | 'supportedFindings' | 'competingHypotheses' | 'nextTest'>
  & { receipts: Array<{ executionReceipt: Record<string, unknown>; evidenceRefs: string[] }> } };
const receiptGroupSchema = z.array(text);
/** Decodes the existing sourceEvidence JSON format only; never interprets narrative text. */
export function publishedReceiptIds(id: string): string[] {
  try {
    const parsed = receiptGroupSchema.safeParse(JSON.parse(id));
    return parsed.success ? parsed.data : [id];
  } catch (cause: unknown) {
    if (cause instanceof SyntaxError) return [id];
    throw new SavedInvestigationError('STORAGE_FAILURE', { cause });
  }
}
function publishedEvidenceIds(content: PublicationRecord['content'], refs: readonly string[],
  aliases: ReadonlyMap<string, string>): string[] {
  const requested = new Set(refs), expanded = new Set(refs);
  for (const receipt of content.receipts) {
    const native = receipt.executionReceipt;
    const identities = [native.id, native.receiptRef, native.receiptId, ...receipt.evidenceRefs]
      .filter((ref): ref is string => typeof ref === 'string');
    if (identities.some(ref => requested.has(ref))) identities.forEach(ref => expanded.add(ref));
  }
  return [...aliases].filter(([id]) => publishedReceiptIds(id).some(ref => expanded.has(ref)))
    .map(([, id]) => id);
}
/** One saved-reader projection. The caller supplies the live Ask egress helpers unchanged. */
export function projectSavedInvestigationResponse(record: PublicationRecord,
  freshness: SavedInvestigationFreshness, projection: PublicProjectors): SavedInvestigationResponse {
  const content = record.content;
  const sources = new Map([...content.numericBindings.map(binding => binding.evidence),
    ...content.evidence].map(evidence => [evidence.id, evidence]));
  const aliases = new Map([...sources.keys()].map((id, index) => [id, `saved-evidence-${index + 1}`]));
  const evidence = (value: AskHelixEvidenceRefDto) => {
    const id = aliases.get(value.id);
    if (id === undefined) throw new SavedInvestigationError('INVALID_EVIDENCE');
    return projection.evidence({ ...value, id });
  };
  const links = (entry: { supportingReceiptRefs: string[]; contradictingReceiptRefs: string[] }) => ({
    supportingEvidenceIds: publishedEvidenceIds(content, entry.supportingReceiptRefs, aliases),
    contradictingEvidenceIds: publishedEvidenceIds(content, entry.contradictingReceiptRefs, aliases),
  });
  const resolved = (value: z.infer<typeof resolvedFact>) => value.kind === 'unknown' ? value : {
    kind: value.kind, value: value.value, evidenceIds: publishedEvidenceIds(content, value.evidenceRefs, aliases),
  };
  const parsed = (projection.schema ?? savedInvestigationResponseSchema).safeParse({ artifact: {
    artifactId: record.artifactId, revision: content.revision, producedAt: content.producedAt,
    asOf: content.asOf, answerMarkdown: content.answerMarkdown, ...projection.metadata,
    ...(content.figureCut ? { figureCut: content.figureCut } : {}),
    numericBindings: content.numericBindings.map(binding => projection.binding({ ...binding,
      evidence: evidence(binding.evidence) })),
    evidence: [...sources.values()].map(evidence),
    supportedFindings: content.supportedFindings.map(entry => ({ text: entry.text, basis: entry.basis, ...links(entry) })),
    competingHypotheses: content.competingHypotheses.map(entry => ({ text: entry.text,
      ...(entry.reasonCode ? { reasonCode: entry.reasonCode } : {}), ...links(entry) })),
    nextTest: { text: content.nextTest.text, owner: resolved(content.nextTest.owner),
      timing: resolved(content.nextTest.timing), ...links(content.nextTest) },
  }, freshness: { state: freshness.state, checkedAt: freshness.checkedAt } });
  if (!parsed.success) throw new SavedInvestigationError('INVALID_EVIDENCE', { cause: parsed.error });
  return parsed.data;
}
export const SAVED_INVESTIGATION_ERROR_CODES = [
  'INVALID_REQUEST', 'INVALID_EVIDENCE', 'UNKNOWN_SUBJECT', 'NOT_FOUND', 'UNAUTHORIZED',
  'CONTENT_CONFLICT', 'REVISION_CONFLICT', 'STORAGE_FAILURE',
] as const;
export type SavedInvestigationErrorCode = typeof SAVED_INVESTIGATION_ERROR_CODES[number];
export const INVESTIGATION_SAVE_REASONS = [
  'NOT_ANSWERED', 'SIDECAR_MISSING', 'SIDECAR_INVALID', 'SIDECAR_TOO_LARGE',
  'SIDECAR_UNBOUND', 'FACT_TOKEN_INVALID', 'UNKNOWN_FACT', 'FIGURE_NOT_SUPPORTED',
  'RUN_PROVENANCE_MISMATCH', 'RECEIPT_MISSING', 'SKILL_MISSING', 'COMPLETION_INVALID',
  'CUT_MISMATCH', 'PIN_SCOPE_MISMATCH', 'PIN_UNRESOLVED', 'PIN_IDENTITY_AMBIGUOUS',
  'PIN_SUBSET_MISMATCH', 'PIN_SOURCE_MISMATCH', 'PIN_PUBLICATION_MISSING',
  'PIN_INVALID', 'PIN_CLOSURE_LIMIT', 'STATUS_ABSENT_FALLBACK', 'EMPTY_INVESTIGATION',
] as const;
export type InvestigationSaveReason = typeof INVESTIGATION_SAVE_REASONS[number];
export const INVESTIGATION_GRAPH_CUT_CODES = ['graph_cut_mismatch', 'graph_cut_unpublished'] as const;
export const investigationGraphCutRefusalSchema = z.object({
  code: z.enum(INVESTIGATION_GRAPH_CUT_CODES),
  details: z.object({ investigationAsOf: z.string().date().optional(),
    requestedAsOf: z.string().date(), admittedCuts: z.array(z.string().date()).optional() }).strict(),
});
export const INVESTIGATION_STOP_CODES = [
  'RUN_TIMEOUT', 'CLIENT_CANCELLED', 'EVIDENCE_BACKEND_UNAVAILABLE', 'RUNNER_FAILURE',
  'PUBLICATION_FAILURE',
] as const;
export const INVESTIGATION_FAILURE_STAGES = [
  'run_timeout', 'cancelled', 'backend_unavailable', 'not_authorized', 'validation',
  'runner', 'publication', 'storage',
  'input_schema', 'definition_scope', 'canonicalization', 'content_schema',
  'storage_encoding', 'serialized_size', 'row_integrity',
] as const;
export const investigationStoreDiagnosticSchema = z.object({
  stage: z.enum(INVESTIGATION_FAILURE_STAGES),
  rule: z.enum(['SCHEMA_INVALID', 'TENANT_MISMATCH', 'NON_JSON_VALUE', 'CONTENT_LIMIT',
    'ROW_IDENTITY_MISMATCH', 'STORAGE_ENCODING_INVALID']),
  path: z.array(z.union([z.string(), z.number()])).optional(),
  issueCode: z.string().optional(), serializedChars: z.number().int().nonnegative().optional(),
  limit: z.number().int().nonnegative().optional(),
});
export type InvestigationStoreDiagnostic = z.infer<typeof investigationStoreDiagnosticSchema>;
export const savedInvestigationSaveStatusSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true) }).strict(),
  z.object({ ok: z.literal(false),
    code: z.enum([...SAVED_INVESTIGATION_ERROR_CODES, ...INVESTIGATION_STOP_CODES, ...INVESTIGATION_GRAPH_CUT_CODES]),
    details: investigationGraphCutRefusalSchema.shape.details.optional(),
    reason: z.enum(INVESTIGATION_SAVE_REASONS).optional(),
    rule: investigationStoreDiagnosticSchema.shape.rule.optional(),
    path: investigationStoreDiagnosticSchema.shape.path,
    issueCode: investigationStoreDiagnosticSchema.shape.issueCode,
    serializedChars: investigationStoreDiagnosticSchema.shape.serializedChars,
    limit: investigationStoreDiagnosticSchema.shape.limit,
    stage: z.enum(INVESTIGATION_FAILURE_STAGES).optional() }).strict(),
]);
export type SavedInvestigationSaveStatus = z.infer<typeof savedInvestigationSaveStatusSchema>;
export class SavedInvestigationError extends Error {
  readonly cause?: unknown;
  readonly code: SavedInvestigationErrorCode;
  readonly reason?: InvestigationSaveReason;
  constructor(code: SavedInvestigationErrorCode, options?: { cause?: unknown; reason?: InvestigationSaveReason }) {
    super(code);
    this.code = code;
    this.cause = options?.cause;
    this.reason = options?.reason;
    this.name = 'SavedInvestigationError';
  }
}
